import React, { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons/faCircleCheck';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Gap } from 'ui-components/components/flex/gap';
import { Modal } from 'ui-components/components/modal';
import { ModalPortal } from '../modal-portal';
import { Text, TextType } from 'ui-components/components/text';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import {
  DEFER_BUY_NOW_MODAL_SHOWN,
  DEFER_BUY_NOW_MODAL_BACK_CLICKED,
  DEFER_BUY_NOW_MODAL_START_CLICKED,
} from '../../constants/analytics-events';
import { useGlobalModalLaunchContext } from '../../contexts/modal-context/global-modal-context';
import { Segment } from '../../services/analytics-services/segment';
import { BuyNowService } from '../../services/buy-now-service';
import Global from '../../store/global-selectors';
import { updateBuyNowSetting } from '../../store/seller/seller.actions';
import { messages } from './messages';
import style from './defer-buy-now-modal.module.css';

import type { MessageDescriptor } from 'react-intl';

type DeferBuyNowModalProps = {
  onClose: () => void;
};

type FeatureItemProps = {
  titleMessage: MessageDescriptor;
  descriptionMessage: MessageDescriptor;
};

const FeatureItem = ({ titleMessage, descriptionMessage }: FeatureItemProps) => (
  <FlexColumn className={style.feature} gap={Gap.Space4}>
    <FlexRow gap={Gap.Space12}>
      <FontAwesomeIcon className={style.checkIcon} icon={faCircleCheck} />
      <Text textType={TextType.Label} value={titleMessage} />
    </FlexRow>
    <Text className={style.featureDescription} textType={TextType.Caption} value={descriptionMessage} />
  </FlexColumn>
);

export const DeferBuyNowModal = ({ onClose }: DeferBuyNowModalProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const buyNowSetting = useSelector(Global.selectSellerBuyNowSetting);
  const { closeManageSubscriptionDrawer } = useGlobalModalLaunchContext() || {};

  useEffect(() => {
    Segment.trackEvent(DEFER_BUY_NOW_MODAL_SHOWN);
  }, []);

  const handleBackToPlan = useCallback(() => {
    Segment.trackEvent(DEFER_BUY_NOW_MODAL_BACK_CLICKED);
    onClose();
  }, [onClose]);

  const handleStartTrial = useCallback(async () => {
    Segment.trackEvent(DEFER_BUY_NOW_MODAL_START_CLICKED);

    try {
      const updatedSetting = await BuyNowService.markFlowCompleted(buyNowSetting);
      dispatch(updateBuyNowSetting(updatedSetting));
    } catch {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        content: intl.formatMessage(messages.errorSavingProgress),
      });
    } finally {
      closeManageSubscriptionDrawer?.();
      onClose();
    }
  }, [closeManageSubscriptionDrawer, onClose, dispatch, buyNowSetting, intl]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <ModalPortal onClose={handleClose}>
      <Modal className={style.modal} modalHeaderId='defer-buy-now-modal-header'>
        <FlexColumn className={style.container} gap={Gap.Space16}>
          <FlexRow className={style.header}>
            <Button
              ariaLabel={intl.formatMessage(messages.closeButton)}
              className={style.closeButton}
              onClick={handleClose}
              theme={THEME_CONSTANTS.unstyled}
            >
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          </FlexRow>

          <FlexColumn className={style.headingSection} gap={Gap.Space12}>
            <Text className={style.heading} textType={TextType.H2} value={messages.heading} />
            <Text className={style.subheading} textType={TextType.H3} value={messages.subheading} />
          </FlexColumn>

          <FlexColumn className={style.featuresSection} gap={Gap.Space16}>
            <Text className={style.featuresHeading} textType={TextType.Label} value={messages.trialIncludesHeading} />

            <FlexColumn gap={Gap.Space24}>
              <FlexRow gap={Gap.Space32}>
                <FeatureItem descriptionMessage={messages.powerfulAutomationDescription} titleMessage={messages.powerfulAutomationTitle} />
                <FeatureItem descriptionMessage={messages.customAnalyticsDescription} titleMessage={messages.customAnalyticsTitle} />
              </FlexRow>

              <FlexRow gap={Gap.Space32}>
                <FeatureItem descriptionMessage={messages.inventoryWarehouseDescription} titleMessage={messages.inventoryWarehouseTitle} />
                <FeatureItem descriptionMessage={messages.returnsExchangesDescription} titleMessage={messages.returnsExchangesTitle} />
              </FlexRow>
            </FlexColumn>
          </FlexColumn>

          <FlexRow className={style.actions} gap={Gap.Space12}>
            <Button onClick={handleBackToPlan} theme={THEME_CONSTANTS.secondary}>
              <Text textType={TextType.Caption} value={messages.backToPlanButton} />
            </Button>
            <Button onClick={handleStartTrial} theme={THEME_CONSTANTS.primary}>
              <Text textType={TextType.Caption} value={messages.startTrialButton} />
            </Button>
          </FlexRow>

          <Text className={style.disclaimer} textType={TextType.Caption} value={messages.disclaimer} />
        </FlexColumn>
      </Modal>
    </ModalPortal>
  );
};
